import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { getEnabledFeatures } from '../services/api';

export const FEATURE_TICKETING = 'ticketing_system';
export const FEATURE_MONTHLY_DUES = 'monthly_dues';

const FeatureFlagsContext = createContext(null);

export function useFeatureFlags() {
  const ctx = useContext(FeatureFlagsContext);
  if (!ctx) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
  }
  return ctx;
}

/** Convenience: whether a feature key is enabled for the current user. */
export function useFeatureEnabled(key) {
  const { isEnabled, loading } = useFeatureFlags();
  return { enabled: isEnabled(key), loading };
}

export function FeatureFlagsProvider({ children }) {
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const [features, setFeatures] = useState({});
  const [loading, setLoading] = useState(true);

  const refreshSeq = useRef(0);
  const refresh = useCallback(async () => {
    const seq = ++refreshSeq.current;
    setLoading(true);
    try {
      const res = await getEnabledFeatures();
      if (seq !== refreshSeq.current) return;
      setFeatures(res.data?.features || {});
    } catch (err) {
      if (seq !== refreshSeq.current) return;
      console.warn('Failed to load feature flags:', err?.message);
      setFeatures({});
    } finally {
      if (seq === refreshSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === 'token' || event.key === null) refresh();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [refresh]);

  useEffect(() => {
    if (authLoading) return;
    refresh();
  }, [authLoading, isAuthenticated, user?.id, refresh]);

  const isEnabled = useCallback(
    (key) => Boolean(features[key]),
    [features]
  );

  const value = useMemo(
    () => ({ features, loading: authLoading || loading, isEnabled, refresh }),
    [features, authLoading, loading, isEnabled, refresh]
  );

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}
